import React, { useMemo, useState } from 'react';
import { FiChevronDown, FiChevronUp, FiSearch } from 'react-icons/fi';
import { formatDateOnlyUTC } from '../../../utils/dateOnly';
import { Estimate } from '../../../hooks/useEstimatesInfinite';
import EstimateStatusBadge from './EstimateStatusBadge';
import { EstimateActions } from './EstimateActions';

interface EstimateTableProps {
  estimates: Estimate[];
  loading?: boolean;
  onEdit: (estimateId: string) => void;
  onView: (estimateId: string) => void;
  onDelete: (estimateId: string) => void;
  onConvert: (estimateId: string) => void;
  onStatusChange: (estimateId: string, status: 'pending' | 'approved' | 'rejected') => void;
  searchTerm?: string;
  onSearchTermChange?: (value: string) => void;
  statusFilter?: string;
  onStatusFilterChange?: (value: string) => void;
}

type SortField = 'number' | 'customer' | 'issue_date' | 'expiry_date' | 'status' | 'total';
type SortDirection = 'asc' | 'desc';

const statusOptions = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' }
];

const EstimateTable: React.FC<EstimateTableProps> = ({
  estimates,
  loading = false,
  onEdit,
  onView,
  onDelete,
  onConvert,
  onStatusChange,
  searchTerm = '',
  onSearchTermChange,
  statusFilter = 'all',
  onStatusFilterChange,
}) => {
  const [sortField, setSortField] = useState<SortField>('number');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const filteredAndSorted = useMemo(() => {
    let next = [...estimates];
    if (statusFilter !== 'all') {
      next = next.filter((estimate) => estimate.status === statusFilter);
    }

    if (searchTerm.trim()) {
      const q = searchTerm.trim().toLowerCase();
      next = next.filter((estimate) => {
        const customerName = (
          estimate.display_name ||
          `${estimate.first_name || ''} ${estimate.last_name || ''}`.trim() ||
          estimate.company_name ||
          ''
        ).toLowerCase();
        const address = `${estimate.billing_addr_line1 || ''} ${estimate.billing_addr_city || ''} ${estimate.billing_addr_state || ''} ${estimate.billing_addr_zip || ''}`.toLowerCase();
        return estimate.number.toLowerCase().includes(q) || customerName.includes(q) || address.includes(q);
      });
    }

    return next.sort((left, right) => {
      let leftValue: any;
      let rightValue: any;
      switch (sortField) {
        case 'number':
          leftValue = left.number;
          rightValue = right.number;
          break;
        case 'customer':
          leftValue = (left.display_name || `${left.first_name || ''} ${left.last_name || ''}`.trim() || left.company_name || '').toLowerCase();
          rightValue = (right.display_name || `${right.first_name || ''} ${right.last_name || ''}`.trim() || right.company_name || '').toLowerCase();
          break;
        case 'issue_date':
          leftValue = new Date(left.issue_date).getTime();
          rightValue = new Date(right.issue_date).getTime();
          break;
        case 'expiry_date':
          leftValue = new Date(left.expiry_date || left.issue_date).getTime();
          rightValue = new Date(right.expiry_date || right.issue_date).getTime();
          break;
        case 'status':
          leftValue = left.status;
          rightValue = right.status;
          break;
        case 'total':
          leftValue = Number(left.total_amount || 0);
          rightValue = Number(right.total_amount || 0);
          break;
      }

      if (typeof leftValue === 'string' && typeof rightValue === 'string') {
        return sortDirection === 'asc'
          ? leftValue.localeCompare(rightValue)
          : rightValue.localeCompare(leftValue);
      }

      return sortDirection === 'asc' ? leftValue - rightValue : rightValue - leftValue;
    });
  }, [estimates, searchTerm, sortField, sortDirection, statusFilter]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((current) => current === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSortField(field);
    setSortDirection('desc');
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc'
      ? <FiChevronUp className="h-4 w-4" />
      : <FiChevronDown className="h-4 w-4" />;
  };

  if (loading) {
    return (
      <div className="rounded-lg bg-white shadow dark:bg-gray-800">
        <div className="space-y-4 p-6">
          {[...Array(5)].map((_, index) => (
            <div key={index} className="h-16 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-white shadow dark:bg-gray-800">
      <div className="border-b border-gray-200 p-4 dark:border-gray-700">
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="flex-1">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by estimate #, name, or address..."
                value={searchTerm}
                onChange={(e) => onSearchTermChange?.(e.target.value)}
                className="w-full rounded-xl border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra dark:border-neutral-700 dark:bg-neutral-900 dark:text-white"
              />
            </div>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => onStatusFilterChange?.(e.target.value)}
            className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra dark:border-neutral-700 dark:bg-neutral-900 dark:text-white"
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {filteredAndSorted.length === 0 ? (
        <div className="py-12 text-center text-gray-500 dark:text-gray-400">
          {searchTerm || statusFilter !== 'all' ? 'No estimates match your filters.' : 'No estimates found.'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-neutral-900">
              <tr>
                {[
                  ['number', 'Estimate #'],
                  ['customer', 'Customer'],
                  ['issue_date', 'Issue Date'],
                  ['expiry_date', 'Expiry Date'],
                  ['status', 'Status'],
                  ['total', 'Total'],
                ].map(([field, label]) => (
                  <th key={field} className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    <button type="button" className="inline-flex items-center gap-1" onClick={() => handleSort(field as SortField)}>
                      {label}
                      {getSortIcon(field as SortField)}
                    </button>
                  </th>
                ))}
                <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-800 dark:bg-gray-800">
              {filteredAndSorted.map((estimate) => {
                const customerName = estimate.display_name || `${estimate.first_name || ''} ${estimate.last_name || ''}`.trim() || estimate.company_name || 'Unknown Customer';
                return (
                  <tr key={estimate.id} className="hover:bg-gray-50 dark:hover:bg-neutral-900/40">
                    <td className="whitespace-nowrap px-6 py-4 text-sm font-semibold">
                      <button
                        type="button"
                        onClick={() => onView(estimate.id)}
                        className="text-sedra hover:underline"
                      >
                        {estimate.number}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-200">{customerName}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700 dark:text-gray-200">{formatDateOnlyUTC(estimate.issue_date)}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700 dark:text-gray-200">{estimate.expiry_date ? formatDateOnlyUTC(estimate.expiry_date) : '-'}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm"><EstimateStatusBadge status={estimate.status} /></td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm font-semibold text-gray-900 dark:text-white">${Number(estimate.total_amount || 0).toFixed(2)}</td>
                    <td className="px-6 py-4 text-right">
                      <EstimateActions
                        estimate={estimate}
                        onEdit={() => onEdit(estimate.id)}
                        onView={() => onView(estimate.id)}
                        onDelete={() => onDelete(estimate.id)}
                        onConvert={() => onConvert(estimate.id)}
                        onStatusChange={(status) => onStatusChange(estimate.id, status)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default EstimateTable;
